import React, { useCallback, useMemo, useState } from 'react';
import { bindPopover, usePopupState } from 'material-ui-popup-state/hooks';

import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useIsUserPermittedTo } from '@occc/utils-access-control';
import { Feature } from '@occc/utils-access-control';
import {
  Allowed_List_Phone_Number_Time_Period,
  AllowListPhoneNumberFilterInput,
  AllowListPhoneNumberFragment,
  DateRangeInput,
} from '@occc/graphql';
import { isFiltersSelected } from '@occc-app/design-system';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useAdvancedQueryParam, useToggle } from '@occc/utils';
import { formatDateTime } from '@occc-app/lib/date';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { Row } from '@occc/ui-design-system';

import { useGetAllowListPhoneNumbers } from './allow-list.query.hook';

import {
  DEFAULT_ALLOW_LIST_PHONE_NUMBERS_TIME_PERIOD,
  allowListSearchParams,
  defaultEndDateQueryParams,
  defaultEnteredOnQueryParams,
  defaultStartDateQueryParams,
  defaultPaginationParams,
} from '../allow-list.constants';
import { AllowListPhoneNumberFilterFormValues } from '../table-filter/table-filter.types';
import { makeFields } from '../allow-list.table.fields';

export const useAllowListTableContainer = () => {
  const [periodFilter, setPeriodFilter] =
    useAdvancedQueryParam<Allowed_List_Phone_Number_Time_Period>(defaultEnteredOnQueryParams);
  const [startDateFilter, setStartDateFilter] = useAdvancedQueryParam<Date | undefined>(
    defaultStartDateQueryParams,
  );
  const [endDateFilter, setEndDateFilter] = useAdvancedQueryParam<Date | undefined>(
    defaultEndDateQueryParams,
  );
  const popupState = usePopupState({ variant: 'popover', popupId: 'filters' });
  const popoverProps = React.useMemo(() => {
    return {
      ...bindPopover({
        ...popupState,
      }),
    };
  }, [popupState]);
  const onFilterFormSubmit = useCallback(
    (values: AllowListPhoneNumberFilterFormValues) => {
      setPeriodFilter(values.period);
      if (values.period === Allowed_List_Phone_Number_Time_Period.Custom) {
        setStartDateFilter(values.startDate);
        setEndDateFilter(values.endDate);
      } else {
        setStartDateFilter(undefined);
        setEndDateFilter(undefined);
      }

      popoverProps.onClose();
    },
    [popoverProps, setEndDateFilter, setPeriodFilter, setStartDateFilter],
  );
  const onFilterFormReset = useCallback(() => {
    setPeriodFilter(DEFAULT_ALLOW_LIST_PHONE_NUMBERS_TIME_PERIOD);
    setStartDateFilter(undefined);
    setEndDateFilter(undefined);

    popoverProps.onClose();
  }, [popoverProps, setEndDateFilter, setPeriodFilter, setStartDateFilter]);
  const isFilterSelected = isFiltersSelected(
    [periodFilter],
    [DEFAULT_ALLOW_LIST_PHONE_NUMBERS_TIME_PERIOD],
  );

  const [search, setSearch] = useSearchParams(allowListSearchParams);
  const [paginationInfo, onPageChange] = usePaginationParams(defaultPaginationParams);
  const isUserPermittedToAddPhoneNumberToAllowList = useIsUserPermittedTo(
    Feature.ALLOW_LIST_CREATE,
  );
  const isUserPermittedToEdit = useIsUserPermittedTo(Feature.ALLOW_LIST_EDIT);
  const isUserPermittedToDelete = useIsUserPermittedTo(Feature.ALLOW_LIST_DELETE);
  const [isUpsertNumberModalVisible, toggleUpsertAllowListPhoneNumberModal] = useToggle();
  const [isCreateMode, setIsAllowListModalCreateMode] = useState(false);
  const [phoneNumberFragment, setPhoneNumberFragment] = useState<
    AllowListPhoneNumberFragment | undefined
  >(undefined);

  const openUpsertAllowListPhoneNumberModal = useCallback(
    (params: { isCreateMode: boolean; entity?: AllowListPhoneNumberFragment }) => {
      setIsAllowListModalCreateMode(params.isCreateMode);
      setPhoneNumberFragment(params.entity);
      toggleUpsertAllowListPhoneNumberModal();
    },
    [toggleUpsertAllowListPhoneNumberModal],
  );

  const filter: AllowListPhoneNumberFilterInput = useMemo((): AllowListPhoneNumberFilterInput => {
    const range: DateRangeInput | undefined =
      periodFilter === Allowed_List_Phone_Number_Time_Period.Custom &&
      startDateFilter &&
      endDateFilter
        ? {
            startDate: formatDateTime(startDateFilter),
            endDate: formatDateTime(endDateFilter),
          }
        : undefined;

    return {
      search: search,
      dateFilter: { period: periodFilter, range },
    };
  }, [endDateFilter, periodFilter, startDateFilter, search]);

  const { loading, error, data, paginationProps } = useGetAllowListPhoneNumbers({
    filter,
    paginationInfo,
    onPageChange,
  });

  const rows: Row[] = useMemo(
    () =>
      data?.map((item: AllowListPhoneNumberFragment) => ({
        key: item.uuid,
        fields: makeFields({
          item,
          isUserPermittedToEdit,
          isUserPermittedToDelete,
          openUpsertAllowListPhoneNumberModal,
        }),
      })) || [],
    [data, isUserPermittedToDelete, isUserPermittedToEdit, openUpsertAllowListPhoneNumberModal],
  );
  const onAddAllowListPhoneNumberClick = useCallback(
    () => openUpsertAllowListPhoneNumberModal({ isCreateMode: true }),
    [openUpsertAllowListPhoneNumberModal],
  );

  return useMemo(
    () => ({
      setSearch,
      search,
      onAddAllowListPhoneNumberClick,
      rows,
      paginationProps,
      loading,
      error,
      isUserPermittedToAddPhoneNumberToAllowList,
      isUpsertNumberModalVisible,
      isCreateMode,
      toggleUpsertAllowListPhoneNumberModal,
      phoneNumberFragment,
      filter: {
        period: periodFilter,
        startDate: startDateFilter,
        endDate: endDateFilter,
      },
      popupState,
      onFilterFormSubmit,
      popoverProps,
      onFilterFormReset,
      isFilterSelected,
    }),
    [
      error,
      isCreateMode,
      isUpsertNumberModalVisible,
      isUserPermittedToAddPhoneNumberToAllowList,
      loading,
      onAddAllowListPhoneNumberClick,
      paginationProps,
      phoneNumberFragment,
      rows,
      search,
      setSearch,
      toggleUpsertAllowListPhoneNumberModal,
      periodFilter,
      startDateFilter,
      endDateFilter,
      popupState,
      onFilterFormSubmit,
      popoverProps,
      onFilterFormReset,
      isFilterSelected,
    ],
  );
};
